import React from "react";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  loadingText?: string;
  /** apple / apple-gray: apple.com's blue and gray pills (pair with size "xl"). */
  variant?: "primary" | "secondary" | "danger" | "ghost" | "theme" | "apple" | "apple-gray";
  size?: "sm" | "md" | "lg" | "xl";
  fullWidth?: boolean;
  children: React.ReactNode;
}

export default function Button({
  loading = false,
  loadingText,
  variant = "primary",
  size = "md",
  fullWidth = false,
  disabled,
  className = "",
  children,
  ...props
}: ButtonProps) {
  const baseClasses = "inline-flex items-center justify-center transition-all duration-200 rounded-full active:scale-[0.98] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed transform-gpu relative overflow-hidden focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#0071E3]/30";

  // Font weight lives per variant: the apple ones use apple.com's regular weight.
  const variantClasses = {
    // Apple Style: Solid High Contrast
    primary: "font-semibold bg-[#1D1D1F] dark:bg-[#F5F5F7] text-[#FFFFFF] dark:text-[#1D1D1F] hover:opacity-[0.85]",
    theme: "font-semibold bg-[#0066CC] text-white hover:bg-[#0071e3] shadow-sm", // Apple Link Blue
    secondary: "font-semibold bg-white/10 dark:bg-white/5 backdrop-blur-md text-[#1D1D1F] dark:text-[#F5F5F7] border border-[#1D1D1F]/10 dark:border-white/10 hover:bg-[#1D1D1F]/5 dark:hover:bg-white/10",
    danger: "font-semibold bg-[#FF3B30] text-white hover:bg-[#ff453a]",
    ghost: "font-semibold bg-transparent hover:bg-[#1D1D1F]/5 dark:hover:bg-white/5 text-[#0066CC]",
    apple: "font-normal bg-[#0071E3] text-white hover:bg-[#0077ED] active:bg-[#006EDB]",
    "apple-gray": "font-normal bg-[#E8E8ED] text-[#1D1D1F] hover:bg-[#DCDCE1] dark:bg-[#3A3A3C] dark:text-[#F5F5F7] dark:hover:bg-[#48484A]",
  };

  const sizeClasses = {
    sm: "px-3.5 py-1.5 text-[11px] tracking-tight",
    md: "px-5 py-2 text-[13px] tracking-tight",
    lg: "px-7 py-3 text-[14px] tracking-tight font-bold",
    xl: "min-w-[6.5rem] px-[18px] py-2.5 text-[15px] sm:min-w-[7.5rem] sm:px-[22px] sm:py-3 sm:text-[17px] leading-[1.18] tracking-[-0.022em] gap-1.5",
  };

  const widthClass = fullWidth ? "w-full" : "";

  const combinedClasses = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${widthClass} ${className}`.trim();

  return (
    <button
      className={combinedClasses}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <div className="flex items-center gap-2">
          <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24">
            <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
            <path className="opacity-100" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" d="M12 2a10 10 0 0 1 10 10"></path>
          </svg>
          {loadingText && <span>{loadingText}</span>}
        </div>
      ) : (
        children
      )}
    </button>
  );
}